import { useState } from "react";
import { useApp } from "@/App";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { BookOpen, Users, Phone, Mail, Trophy, Shield, ChevronDown, KeyRound, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const apiClient = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL || "" });

export default function InfoPage() {
  const { adminSettings, clubs, loading } = useApp();
  const [expandedClub, setExpandedClub] = useState(null);
  
  // Change PIN state
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [changingPin, setChangingPin] = useState(false);
  const [pinChanged, setPinChanged] = useState(false);

  const toggleClub = (clubId) => {
    setExpandedClub(prev => prev === clubId ? null : clubId);
  };

  const pinFormatValid = (pin) => /^[A-Za-z]{3}[0-9]{4}$/.test(pin);

  const handleChangePin = async () => {
    if (!pinFormatValid(currentPin)) { toast.error("Current PIN must be 3 letters + 4 numbers"); return; }
    if (!pinFormatValid(newPin)) { toast.error("New PIN must be 3 letters + 4 numbers"); return; }
    if (newPin !== confirmPin) { toast.error("New PINs don't match"); return; }
    if (currentPin.toUpperCase() === newPin.toUpperCase()) { toast.error("New PIN must be different"); return; }
    
    setChangingPin(true);
    try {
      const res = await apiClient.post("/clubs/change-pin", { current_pin: currentPin, new_pin: newPin });
      toast.success(`PIN changed for ${res.data.club_name}`);
      setPinChanged(true);
      setCurrentPin("");
      setNewPin("");
      setConfirmPin("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change PIN");
    } finally {
      setChangingPin(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="info-page">
      {/* Header */}
      <div className="bg-primary px-4 pt-8 pb-6">
        <h1 className="text-2xl font-extrabold text-white tracking-tight">Information</h1>
      </div>

      <div className="px-4 py-4 pb-24">
        <Tabs defaultValue="contacts" className="w-full">
          <TabsList className="w-full grid grid-cols-3 mb-4 h-12">
            <TabsTrigger 
              value="contacts" 
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground h-10"
              data-testid="contacts-tab"
            >
              <Users size={16} className="mr-2" />
              Contacts
            </TabsTrigger>
            <TabsTrigger 
              value="rules" 
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground h-10"
              data-testid="rules-tab"
            >
              <BookOpen size={16} className="mr-2" />
              Rules
            </TabsTrigger>
            <TabsTrigger 
              value="pin" 
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground h-10"
              data-testid="pin-tab"
            >
              <KeyRound size={16} className="mr-2" />
              Change PIN
            </TabsTrigger>
          </TabsList>

          {/* Rules Tab */}
          <TabsContent value="rules" className="space-y-4" data-testid="rules-content">
            <div className="bg-white rounded-lg border border-border p-4">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen size={20} className="text-primary" />
                <h2 className="font-semibold">League Rules</h2>
              </div>
              
              {loading ? (
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-5/6" />
                </div>
              ) : adminSettings?.rules ? (
                <div className="prose prose-sm max-w-none">
                  {adminSettings.rules.split('\n').map((line, index) => (
                    <p key={index} className="text-sm text-muted-foreground mb-2">
                      {line}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No rules have been set yet. Please contact the league administrator.
                </p>
              )}
            </div>

            {/* Points System */}
            <div className="bg-white rounded-lg border border-border p-4">
              <h3 className="font-semibold mb-3">Points System</h3>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-green-600">2</p>
                  <p className="text-xs text-muted-foreground">Win</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-amber-600">1</p>
                  <p className="text-xs text-muted-foreground">Draw</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-red-500">0</p>
                  <p className="text-xs text-muted-foreground">Loss</p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts" className="space-y-4" data-testid="contacts-content">
            {/* League Contact */}
            <div className="bg-white rounded-lg border border-border p-4">
              <div className="flex items-center gap-2 mb-4">
                <Phone size={20} className="text-primary" />
                <h2 className="font-semibold">League Contact</h2>
              </div>
              
              {loading ? (
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              ) : adminSettings?.contacts_info ? (
                <div className="prose prose-sm max-w-none">
                  {adminSettings.contacts_info.split('\n').map((line, index) => (
                    <p key={index} className="text-sm text-muted-foreground mb-2">
                      {line}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No contact information available yet.
                </p>
              )}
            </div>

            {/* Club Contacts */}
            <div className="bg-white rounded-lg border border-border p-4">
              <h2 className="font-semibold mb-4">Club Contacts</h2>
              
              {loading ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : clubs.length === 0 ? (
                <p className="text-sm text-muted-foreground">No clubs registered yet.</p>
              ) : (
                <div className="space-y-2">
                  {clubs.map((club) => {
                    const isExpanded = expandedClub === club.id;
                    const hasCaptain = club.captain_name || club.captain_email || club.captain_phone;
                    const hasAdmin = club.admin_name || club.admin_email || club.admin_phone;
                    const hasContacts = hasCaptain || hasAdmin;

                    return (
                      <div 
                        key={club.id} 
                        className="border border-border rounded-lg overflow-hidden"
                        data-testid={`club-contact-${club.id}`}
                      >
                        <button
                          onClick={() => toggleClub(club.id)}
                          className="w-full flex items-center justify-between p-3 text-left hover:bg-muted/50 transition-colors"
                          data-testid={`club-contact-toggle-${club.id}`}
                        >
                          <p className="font-medium text-sm text-primary">{club.name}</p>
                          <ChevronDown 
                            size={16} 
                            className={`text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                          />
                        </button>

                        {isExpanded && (
                          <div className="px-3 pb-3 space-y-3 border-t border-border pt-3" data-testid={`club-contact-details-${club.id}`}>
                            {!hasContacts && (
                              <p className="text-xs text-muted-foreground">No contact information added yet.</p>
                            )}

                            {hasCaptain && (
                              <div>
                                <p className="text-xs font-semibold text-primary flex items-center gap-1.5 mb-1.5">
                                  <Trophy size={12} /> Team Captain
                                </p>
                                {club.captain_name && (
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Users size={12} />
                                    <span>{club.captain_name}</span>
                                  </div>
                                )}
                                {club.captain_email && (
                                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                    <Mail size={12} />
                                    <a href={`mailto:${club.captain_email}`} className="hover:underline">{club.captain_email}</a>
                                  </div>
                                )}
                                {club.captain_phone && (
                                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                    <Phone size={12} />
                                    <a href={`tel:${club.captain_phone}`} className="hover:underline">{club.captain_phone}</a>
                                  </div>
                                )}
                              </div>
                            )}

                            {hasAdmin && (
                              <div>
                                <p className="text-xs font-semibold text-primary flex items-center gap-1.5 mb-1.5">
                                  <Shield size={12} /> Club Administrator
                                </p>
                                {club.admin_name && (
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Users size={12} />
                                    <span>{club.admin_name}</span>
                                  </div>
                                )}
                                {club.admin_email && (
                                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                    <Mail size={12} />
                                    <a href={`mailto:${club.admin_email}`} className="hover:underline">{club.admin_email}</a>
                                  </div>
                                )}
                                {club.admin_phone && (
                                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                    <Phone size={12} />
                                    <a href={`tel:${club.admin_phone}`} className="hover:underline">{club.admin_phone}</a>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Change PIN Tab */}
          <TabsContent value="pin" className="space-y-4 pb-20" data-testid="pin-content">
            <div className="bg-white rounded-lg border border-border p-4">
              <h2 className="font-semibold mb-1">Change Club PIN</h2>
              <p className="text-xs text-muted-foreground mb-4">Enter your current PIN to identify your club, then choose a new PIN. Format: 3 letters + 4 numbers.</p>
              
              {pinChanged ? (
                <div className="text-center py-6">
                  <CheckCircle size={48} className="mx-auto text-green-600 mb-3" />
                  <p className="font-semibold text-green-800">PIN changed successfully</p>
                  <p className="text-sm text-muted-foreground mt-1">Use your new PIN for future logins.</p>
                  <Button variant="outline" className="mt-4" onClick={() => setPinChanged(false)} data-testid="change-pin-again">
                    Change Another PIN
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label>Current PIN</Label>
                    <Input 
                      value={currentPin} 
                      onChange={(e) => setCurrentPin(e.target.value.toUpperCase())} 
                      placeholder="e.g., BET0000" 
                      maxLength={7}
                      className="uppercase"
                      data-testid="current-pin-input"
                    />
                  </div>
                  <div>
                    <Label>New PIN</Label>
                    <Input 
                      value={newPin} 
                      onChange={(e) => setNewPin(e.target.value.toUpperCase())} 
                      placeholder="e.g., BET1234" 
                      maxLength={7}
                      className="uppercase"
                      data-testid="new-pin-input"
                    />
                  </div>
                  <div>
                    <Label>Confirm New PIN</Label>
                    <Input 
                      value={confirmPin} 
                      onChange={(e) => setConfirmPin(e.target.value.toUpperCase())} 
                      placeholder="Re-enter new PIN" 
                      maxLength={7}
                      className="uppercase"
                      data-testid="confirm-pin-input"
                    />
                  </div>
                  <Button 
                    onClick={handleChangePin} 
                    disabled={changingPin || !currentPin || !newPin || !confirmPin}
                    className="w-full"
                    data-testid="change-pin-submit"
                  >
                    {changingPin ? "Changing..." : "Change PIN"}
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
